'use strict';

import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Checkbox } from 'react-bootstrap';
const uuid = require('uuid');
const semver = require('semver');

const Strings = require('../../../../app/shared/Strings');
const ActivityActions = require('../../actions/ActivityActions');
const ActivityStore = require('../../stores/activity_store_reflux');
const ForgeError = require('../../util/error');
import ErrorBox from '../common/ErrorBox';
import WarningBox from '../common/WarningBox';
import TagHelp from '../Tags/TagHelp';
import LoadingSpinner from '../loading/LoadingSpinner';
import WarningIcon from '../common/WarningIcon';
import Tooltip from '../common/Tooltip';
import ValidationReport from '../validation/ValidationReport';
import ConfirmModal from './ConfirmModal';

const propTypes = {
    onClose: PropTypes.func.isRequired,
    activityId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    channelId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    show: PropTypes.bool,
    activityLocked: PropTypes.bool
};

const defaultProps = {
    show: true,
    activityLocked: false
};

class PublishActivityModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            publishError: null,
            isBreakingError: null,
            validateError: null,
            publishActionId: null,
            isBreakingActionId: null,
            validationActionId: null,
            activityStoreState: ActivityStore.getStateActivity(props.activityId, props.projectId, props.channelId),
            publishMessage: '',
            mandatedBumpMajorVersion: false,
            lastestMatchingMajorVersion: null,
            validateProhibitsPublish: false,
            bumpMajorVersion: false,
            updateInactiveAssignments: true,
            updateActiveAssignments: true,
            blockAssignmentUpdate: false,
            showConfirmModal: false,
        };

        this.forceRecheckCompatibility = this.forceRecheckCompatibility.bind(this);
        this.onChangePublishMessage = this.onChangePublishMessage.bind(this);
        this.onPublish = this.onPublish.bind(this);
        this.onUpdateInactiveChange = this.onUpdateInactiveChange.bind(this);
        this.onUpdateActiveChange = this.onUpdateActiveChange.bind(this);
        this.onBumpMajorVersionChange = this.onBumpMajorVersionChange.bind(this);
        this.setConfirmModalState = this.setConfirmModalState.bind(this);
        this.isUpdateInactiveAllowed = this.isUpdateInactiveAllowed.bind(this);
        this.isUpdateActiveAllowed = this.isUpdateActiveAllowed.bind(this);
        this.unsubscribes = [ActivityStore.listen(this.onActivityStoreChange.bind(this))];
    }

    componentDidMount() {
        if (!this.state.activityStoreState.initialized) {
            ActivityActions.getActivity(uuid.v4(), { activityId: this.props.activityId });
        }

        const isBreakingActionId = uuid.v4();
        const validationActionId = uuid.v4();
        this.setState({ isBreakingActionId: isBreakingActionId, validationActionId: validationActionId }, () => {
            ActivityActions.publishIsBreaking(isBreakingActionId, { activityId: this.props.activityId });
            ActivityActions.publishGetValidation(validationActionId, { activityId: this.props.activityId });
        });
    }

    componentDidUpdate(prevProps) {
        // this isn't absolutely necessary, as version difference checking is a more accurate gauge of whether a re-check needs to happen
        // however, activity lock polling is more frequent than activity info polling, so this may catch the need to re-check more quickly
        if (prevProps.activityLocked && !this.props.activityLocked) {
            this.forceRecheckCompatibility();
        }
    }

    componentWillUnmount() {
        this.unsubscribes.forEach(u => u());
    }

    onActivityStoreChange(newState) {
        let recheckCompatibility = false;
        let additionalState = {};
        const activityStoreState = ActivityStore.getStateActivity(this.props.activityId, this.props.projectId, this.props.channelId);
        if (newState.action.id === this.state.publishActionId) {
            if (!newState.action.error) {
                let traits = activityStoreState.publishValidation.traits;
                if (this.props.onPostPublish && traits && traits.hasPostPublishView) {
                    // this.props.onPostPublish(); // redmine #7359 commented out ( per Jeff on discord 6/1/2026 )
                }
                this.props.onClose();
                return;
            }
            else {
                additionalState = { ...additionalState, publishActionId: null, publishError: newState.action.error, showConfirmModal: false };
            }
        }

        if (newState.action.id === this.state.isBreakingActionId) {
            if (!newState.action.error) {
                additionalState = {
                    ...additionalState,
                    mandatedBumpMajorVersion: activityStoreState.publishCompatibility.isBreaking,
                    lastestMatchingMajorVersion: activityStoreState.publishCompatibility.lastestMatchingMajorVersion,
                    updateInactiveAssignments: !!activityStoreState.publishCompatibility.lastestMatchingMajorVersion, // has latest matching major, otherwise no publishes
                    updateActiveAssignments: !!activityStoreState.publishCompatibility.lastestMatchingMajorVersion && !activityStoreState.publishCompatibility.isBreaking && !this.state.bumpMajorVersion,
                };

                if (activityStoreState.publishCompatibility.blockAssignmentUpdate) {
                    additionalState = {
                        ...additionalState,
                        blockAssignmentUpdate: true,
                        updateInactiveAssignments: false,
                        updateActiveAssignments: false,
                    }
                }
            }
            else {
                additionalState = { ...additionalState, isBreakingError: newState.action.error };
            }

            additionalState = { ...additionalState, isBreakingActionId: null };
        }

        if (newState.action.id === this.state.validationActionId) {
            if (!newState.action.error) {
                additionalState = {
                    ...additionalState,
                    validateProhibitsPublish: !activityStoreState.publishValidation.canPublish
                };
            }
            else {
                additionalState = { ...additionalState, validateError: newState.action.error };
            }

            additionalState = { ...additionalState, validationActionId: null };
        }

        if (this.state.activityStoreState.activity && this.state.activityStoreState.activity.version !== activityStoreState.activity.version) {
            recheckCompatibility = true;
        }

        this.setState({ activityStoreState: activityStoreState, ...additionalState }, () => {
            if (recheckCompatibility) this.forceRecheckCompatibility();
        });
    }

    forceRecheckCompatibility() {
        const isBreakingActionId = uuid.v4();
        const validationActionId = uuid.v4();
        this.setState({
                mandatedBumpMajorVersion: false,
                validateProhibitsPublish: false,
                lastestMatchingMajorVersion: null,
                bumpMajorVersion: false,
                updateActiveAssignments: true,
                updateInactiveAssignments: true,
                isBreakingActionId: isBreakingActionId,
                validationActionId: validationActionId,
                isBreakingError: null,
                validateError: null,
                showConfirmModal: false,
            },
            () => {
                ActivityActions.publishIsBreaking(isBreakingActionId, { activityId: this.props.activityId, refresh: true });
                ActivityActions.publishGetValidation(validationActionId, { activityId: this.props.activityId, refresh: true });
            });
    }

    onChangePublishMessage(e) {
        this.setState({ publishMessage: e.target.value });
    }

    onPublish() {
        const actionId = uuid.v4();
        this.setState({ publishActionId: actionId, publishError: null },
            ActivityActions.publishActivity(actionId, {
                activityId: this.props.activityId,
                message: this.state.publishMessage,
                forceBumpMajor: !!this.state.lastestMatchingMajorVersion ? this.state.mandatedBumpMajorVersion || this.state.bumpMajorVersion || false : false,
                updateInactiveAssignments: !!this.state.lastestMatchingMajorVersion && !this.state.blockAssignmentUpdate ? this.state.updateInactiveAssignments : false,
                updateActiveAssignments: !this.state.mandatedBumpMajorVersion && !this.state.bumpMajorVersion && !!this.state.lastestMatchingMajorVersion && !this.state.blockAssignmentUpdate ? this.state.updateActiveAssignments : false
            })
        );
    }

    onUpdateInactiveChange(e) {
        this.setState({ updateInactiveAssignments: e.target.checked });
    }

    onUpdateActiveChange(e) {
        this.setState({ updateActiveAssignments: e.target.checked });
    }

    onBumpMajorVersionChange(e) {
        return; // TEMPORARY disable this feature

        // let additionalState = {};
        // if (e.target.checked) {
        //     additionalState = { updateActiveAssignments: false }
        // }

        // this.setState({ bumpMajorVersion: e.target.checked, ...additionalState });
    }

    setConfirmModalState(showBool = true) {
        this.setState({ showConfirmModal: showBool });
    }

    isUpdateInactiveAllowed() {
        return !!this.state.lastestMatchingMajorVersion && !this.state.blockAssignmentUpdate;
    }

    isUpdateActiveAllowed() {
        return !!this.state.lastestMatchingMajorVersion && !this.state.blockAssignmentUpdate && !this.state.mandatedBumpMajorVersion && !this.state.bumpMajorVersion;
    }

    render() {
        const self = this;

        const pendingPublishCompatibility = !this.state.activityStoreState.initialized || !this.state.activityStoreState.publishCompatibility || !!this.state.isBreakingActionId;
        const pendingValidationInformation = !this.state.activityStoreState.initialized || !this.state.activityStoreState.publishValidation || !!this.state.validationActionId;
        let nextVersion;
        let coercedSemverNext;
        let compatibleVersionRange;
        if (!pendingPublishCompatibility) {
            nextVersion = this.state.mandatedBumpMajorVersion || this.state.bumpMajorVersion ? this.state.activityStoreState.publishCompatibility.bumpedVersion : this.state.activityStoreState.publishCompatibility.version;
            coercedSemverNext = semver.coerce(nextVersion);
            if (coercedSemverNext) {
                compatibleVersionRange = `${coercedSemverNext.major}.x.x`;
            }
            else {
                console.warn(`semver unable to coerce nextVersion: "${nextVersion}"`);
            }
        }

        const updateInactiveSelected = this.isUpdateInactiveAllowed() ? this.state.updateInactiveAssignments : false;
        const updateActiveSelected = this.isUpdateActiveAllowed() ? this.state.updateActiveAssignments : false;
        const updateInactiveTooltip = <span>{Strings['en'].activity.publish.TT_UPDATE_INACTIVE}</span>;
        const updateActiveTooltip = <span>{Strings['en'].activity.publish.TT_UPDATE_ACTIVE} {compatibleVersionRange && `(${compatibleVersionRange})`}</span>;

        // confirm modal will NOT be displayed if this is the initial publish
        const publishBtnFxn = (!this.state.mandatedBumpMajorVersion && !this.state.lastestMatchingMajorVersion) ? this.onPublish : this.setConfirmModalState;

        if (this.state.showConfirmModal) {
            return (
                <ConfirmModal
                    backdrop="static"
                    enforceFocus
                    restoreFocus
                    modalTitle={Strings['en'].activity.publish.CONFIRM_HEADING}
                    closeHandler={this.setConfirmModalState.bind(this, false)}
                    cancelButtonText={Strings['en'].common.CANCEL_LC}
                    cancelHandler={this.setConfirmModalState.bind(this, false)}
                    buttonText={Strings['en'].activity.publish.CONFIRM_BUTTON}
                    confirmHandler={this.onPublish}
                    confirmButtonBsStyle="danger"
                    text={(
                        <div>{Strings['en'].activity.publish.CONFIRM_PROMPT}
                            <ul className="listBulletIndent__spacedMd margin-top-xl margin-bottom-lg">
                                {(this.state.mandatedBumpMajorVersion || this.state.bumpMajorVersion) &&
                                    <li>{Strings['en'].activity.publish.VERSION_UPDATED_TO} {this.state.activityStoreState.publishCompatibility.bumpedVersion}.</li>
                                }
                                <li>
                                    {(updateInactiveSelected) ?
                                        Strings['en'].activity.publish.CONFIRM_INACTIVE_UPDATE_TRUE
                                        :
                                        Strings['en'].activity.publish.CONFIRM_INACTIVE_UPDATE_FALSE
                                    }  <Tooltip id="confirm-inactive-tt" placement="right">{updateInactiveTooltip}</Tooltip>
                                </li>
                                <li>
                                    {(updateActiveSelected) ?
                                        Strings['en'].activity.publish.CONFIRM_ACTIVE_UPDATE_TRUE
                                        :
                                        Strings['en'].activity.publish.CONFIRM_ACTIVE_UPDATE_FALSE
                                    } <Tooltip id="confirm-active-tt" placement="right">{updateActiveTooltip}</Tooltip>
                                </li>
                            </ul>
                        </div>
                    )}
                />
            );
        }
        else
            return (
                <Modal
                    show={this.props.show}
                    enforceFocus={false}
                    backdrop={false}
                    animation={false}
                    keyboard
                    onHide={this.props.onClose}
                >
                    <Modal.Header closeButton onHide={this.props.onClose}>
                        <Modal.Title>{Strings['en'].activity.publish.MODAL_HEADER}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <ErrorBox error={this.state.isBreakingError} />
                        <ErrorBox error={this.state.validateError} />
                        <ErrorBox error={this.state.publishError} />
                        {(this.props.activityLocked) &&
                            <WarningBox>{Strings['en'].activity.publish.BLOCKED_BY_LOCK}</WarningBox>
                        }
                        {(this.state.activityStoreState.initialized && !this.props.activityLocked) &&
                            <Form>
                                <h2>Compatibility</h2>
                                <hr className="thick hr-color-fg"/>
                                <div className="margin-bottom-xl">
                                    {(pendingPublishCompatibility || pendingValidationInformation) ?
                                        <div>
                                            <LoadingSpinner /> {Strings['en'].activity.publish.CHECKING_MSG}
                                        </div>
                                        :
                                        <div className="font-size-md">
                                            {(!this.state.mandatedBumpMajorVersion) ?
                                                (!!this.state.lastestMatchingMajorVersion) ?
                                                    <div>
                                                        <div className="margin-bottom-md">
                                                            Current working version {this.state.activityStoreState.publishCompatibility.version} is compatible with previously published version {this.state.activityStoreState.publishCompatibility.lastestMatchingMajorVersion}.
                                                        </div>
                                                        {/*
                                                        <Checkbox
                                                        checked={this.state.bumpMajorVersion}
                                                        onChange={this.onBumpMajorVersionChange}
                                                        >
                                                            Bump activity major version anyway <Tooltip id="bumpanyway-tt" placement="right">Bumping major version will disable the option to update active assignments.</Tooltip>
                                                        </Checkbox>
                                                        */}
                                                    </div>
                                                    :
                                                    <div>
                                                        Version {this.state.activityStoreState.publishCompatibility.version} will be published as the initial version.
                                                    </div>
                                                :
                                                <div>
                                                    <div className="margin-bottom-md">
                                                        <WarningIcon /> Current working version {this.state.activityStoreState.publishCompatibility.version} is NOT compatible with previously published version {this.state.activityStoreState.publishCompatibility.lastestMatchingMajorVersion}.
                                                    </div>
                                                </div>
                                            }
                                            {(this.state.mandatedBumpMajorVersion || this.state.bumpMajorVersion) &&
                                                <ul className="listBulletIndent">
                                                    <li>{Strings['en'].activity.publish.VERSION_UPDATED_TO} {this.state.activityStoreState.publishCompatibility.bumpedVersion}.</li>
                                                    <li>Any active assignments using this activity will not be updated.</li>
                                                    {(this.state.blockAssignmentUpdate) &&
                                                        <li>Any inactive assignments using this activity will not be updated.</li>
                                                    }
                                                </ul>
                                            }
                                        </div>
                                    }
                                </div>
                                {(!pendingPublishCompatibility && !pendingValidationInformation) &&
                                    <div>
                                        <h2>Publish Message</h2>
                                        <hr className="thick hr-color-fg" />
                                        <div className="margin-bottom-xl">
                                            <Form.Group controlId="modal-input">
                                                <Form.Label>Publish Message for {this.state.activityStoreState.activity.meta_data.title} (optional)</Form.Label>
                                                <Form.Control
                                                    value={this.state.publishMessage}
                                                    placeholder="This was published because..."
                                                    onChange={this.onChangePublishMessage}
                                                />
                                            </Form.Group>
                                        </div>
                                    </div>
                                }
                                {(!pendingPublishCompatibility && !pendingValidationInformation && this.state.activityStoreState.publishValidation.implemented) &&
                                    <div>
                                        <h2>Validation Information</h2>
                                        <hr className="thick hr-color-fg" />
                                        <div className="margin-bottom-xl">
                                            <ValidationReport
                                                className="mzl-cs-workarea"
                                                validationObj={this.state.activityStoreState.publishValidation}
                                            />
                                        </div>
                                    </div>
                                }
                                {(!pendingPublishCompatibility && !pendingValidationInformation && !!this.state.lastestMatchingMajorVersion) &&
                                    <div>
                                        <h2>Assignment Options</h2>
                                        <hr className="thick hr-color-fg" />
                                        <div className="margin-bottom-xl">
                                            <Form.Group>
                                                <Form.Check>
                                                    <Form.Check.Input
                                                        id="pub-update-inactive"
                                                        checked={updateInactiveSelected}
                                                        onChange={this.onUpdateInactiveChange}
                                                        disabled={!this.isUpdateInactiveAllowed()}
                                                    />
                                                    <Form.Check.Label htmlFor="pub-update-inactive" bsPrefix="form-check-label margin-left-sm">
                                                        {Strings['en'].activity.publish.UPDATE_INACTIVE_ASSN} <Tooltip id="inactive-tt" placement="right">{updateInactiveTooltip}</Tooltip>
                                                    </Form.Check.Label>
                                                </Form.Check>
                                                <Form.Check>
                                                    <Form.Check.Input
                                                        id="pub-update-active"
                                                        checked={updateActiveSelected}
                                                        onChange={this.onUpdateActiveChange}
                                                        disabled={!this.isUpdateActiveAllowed()}
                                                    />
                                                    <Form.Check.Label htmlFor="pub-update-active" bsPrefix="form-check-label margin-left-sm">
                                                        {Strings['en'].activity.publish.UPDATE_ACTIVE_ASSN} <Tooltip id="active-tt" placement="right">{updateActiveTooltip}</Tooltip>
                                                    </Form.Check.Label>
                                                </Form.Check>
                                            </Form.Group>
                                        </div>
                                    </div>
                                }
                            </Form>
                        }
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            disabled={!!this.state.publishActionId
                                || pendingPublishCompatibility
                                || this.props.activityLocked
                                || !!this.state.isBreakingError
                                || !!this.state.validateError
                                || this.state.validateProhibitsPublish
                            }
                            onClick={publishBtnFxn}
                        >
                            Publish
                        </Button>
                    </Modal.Footer>

                </Modal>
            );
    }
}

PublishActivityModal.propTypes = propTypes;
PublishActivityModal.defaultProps = defaultProps;

export default PublishActivityModal;